import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LanguageToggle from './LanguageToggle';
import { useThemeColors } from '../hooks/useThemeColors';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

export default function Login() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [resetUsername, setResetUsername] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useThemeColors();

  React.useEffect(() => {
    const lng = searchParams.get('lng');
    if (lng && (lng === 'en' || lng === 'es')) {
      i18n.changeLanguage(lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(username, password);

      // If user has a language preference, set it automatically
      if (response?.user?.language) {
        i18n.changeLanguage(response.user.language);
      }

      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || t('login.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResetCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/password-reset/request-self-service', {
        username: resetUsername
      });

      // No code displayed - it's sent via email
      setResetStep(1);
    } catch (err) {
      setError(err.response?.data?.error || t('login.failedToSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError(t('login.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('login.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      await api.post('/password-reset/reset-with-code', {
        username: resetUsername,
        code: enteredCode,
        newPassword: newPassword
      });

      setResetStep(2);
    } catch (err) {
      setError(err.response?.data?.error || t('login.failedToResetPassword'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetStep(0);
    setResetUsername('');
    setEnteredCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <LanguageToggle />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img
              src={isDark ? "/sleeptronic-logo-white.png" : "/sleeptronic-logo.png"}
              alt="Sleeptronic Logo"
              style={{ maxWidth: '250px', height: 'auto' }}
            />
          </Box>
          <Typography variant="h6" gutterBottom align="center" color="textSecondary">
            {t('login.maintenanceSystem')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {!showForgotPassword ? (
            <form onSubmit={handleSubmit}>
              <TextField
                label={t('login.username')}
                fullWidth
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
              <TextField
                label={t('login.password')}
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3 }}
                disabled={loading}
              >
                {loading ? t('login.loggingIn') : t('login.loginButton')}
              </Button>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowForgotPassword(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  {t('login.forgotPassword')}
                </Link>
              </Box>
            </form>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Stepper activeStep={resetStep} sx={{ mb: 3 }}>
                <Step>
                  <StepLabel>{t('login.requestCodeStep')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('login.resetPasswordStep')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{t('login.completeStep')}</StepLabel>
                </Step>
              </Stepper>

              {resetStep === 0 && (
                <form onSubmit={handleRequestResetCode}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('login.resetInstructions')}
                  </Typography>
                  <TextField
                    label={t('login.username')}
                    fullWidth
                    margin="normal"
                    value={resetUsername}
                    onChange={(e) => setResetUsername(e.target.value)}
                    required
                    autoFocus
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 3 }}
                    disabled={loading}
                  >
                    {loading ? t('login.sendingCode') : t('login.sendResetCode')}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    sx={{ mt: 2 }}
                    onClick={handleBackToLogin}
                  >
                    {t('login.backToLogin')}
                  </Button>
                </form>
              )}

              {resetStep === 1 && (
                <form onSubmit={handleVerifyAndReset}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {t('login.resetCodeSentTitle')}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {t('login.resetCodeSentMessage')}
                    </Typography>
                  </Alert>
                  <TextField
                    label={t('login.enterResetCode')}
                    fullWidth
                    margin="normal"
                    value={enteredCode}
                    onChange={(e) => setEnteredCode(e.target.value)}
                    required
                    autoFocus
                    placeholder="000000"
                  />
                  <TextField
                    label={t('login.newPassword')}
                    type="password"
                    fullWidth
                    margin="normal"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    helperText={t('login.minimumCharacters')}
                  />
                  <TextField
                    label={t('login.confirmNewPassword')}
                    type="password"
                    fullWidth
                    margin="normal"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 3 }}
                    disabled={loading}
                  >
                    {loading ? t('login.resettingPassword') : t('login.resetPasswordButton')}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    sx={{ mt: 2 }}
                    onClick={handleBackToLogin}
                  >
                    {t('login.backToLogin')}
                  </Button>
                </form>
              )}

              {resetStep === 2 && (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {t('login.passwordResetSuccess')}
                  </Alert>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {t('login.loginWithNewPassword')}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleBackToLogin}
                  >
                    {t('login.backToLogin')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
